import { useCallback, useMemo } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Variant } from 'shared/types/product';
import Typography from 'components/gruenland/atoms/typography';
import { ChevronDown } from 'components/gruenland/icons/ChevronDown';
import { ColorGroupCodeDot } from 'components/gruenland/molecules/colors/ColorGroupCodeDot';
import { discardRepeatedValues } from './helpers/discardRepeatedValues';
import { filterAttributeBasedVariants } from './helpers/filterAttributeBasedVariants';

type Props = {
  currentVariant: Variant;
  variants: Variant[];
  onClick?: (sku: string) => void;
};

const ColorPicker: React.FC<Props> = ({ currentVariant, variants, onClick }) => {
  const ATTRIBUTE = 'colourGroupCode';
  const currentValue = currentVariant.attributes?.[ATTRIBUTE];
  const attributeToFilterTo = 'size';

  const compareVariants = useCallback((a: Variant, b: Variant) => {
    const first = a.attributes?.[ATTRIBUTE];
    const second = b.attributes?.[ATTRIBUTE];
    if (first > second) return 1;
    if (first < second) return -1;
    return 0;
  }, []);

  const variantsToUse: Variant[] = useMemo(() => {
    const result = currentValue
      ? filterAttributeBasedVariants(variants, currentVariant, attributeToFilterTo)
      : discardRepeatedValues(variants, ATTRIBUTE);
    return [...(result || [])].sort(compareVariants);
  }, [attributeToFilterTo, currentValue, currentVariant, variants, compareVariants]);

  return (
    <Menu as="div" className="relative rounded-lg border border-neutrals-black-20">
      <>
        <MenuButton className="relative z-20 flex min-h-[64px] w-full items-center gap-[30px] p-3 [&>svg]:data-[open]:rotate-180">
          <ColorGroupCodeDot
            as="div"
            colourGroupCode={currentVariant.attributes?.colourGroupCode}
            disableHover={true}
          />
          <span className="grow text-start">
            <Typography>{currentVariant.attributes?.colour}</Typography>
          </span>
          <ChevronDown className="transition-transform" />
        </MenuButton>
        <MenuItems
          transition
          modal={false}
          className="z-20 origin-top transition ease-out data-[closed]:scale-95 data-[closed]:opacity-0 lg:absolute lg:inset-x-0 lg:top-0 lg:translate-y-[64px] lg:rounded-md lg:bg-white lg:shadow-lg"
        >
          {variantsToUse.map(({ attributes, id, sku }) => (
            <MenuItem key={id}>
              <div className="flex cursor-pointer items-center gap-[30px] p-3" onClick={() => onClick?.(sku)}>
                <ColorGroupCodeDot as="div" colourGroupCode={attributes?.colourGroupCode} disableHover={true} />
                <Typography>{attributes?.colour}</Typography>
              </div>
            </MenuItem>
          ))}
        </MenuItems>
      </>
    </Menu>
  );
};

export default ColorPicker;
